import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection, query, orderBy, onSnapshot, deleteDoc, doc, where, updateDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';
import { useOrgData } from '../../hooks/useOrgData';
import { logAudit } from '../../utils/auditLog';
import TransactionForm from './TransactionForm';
import ImportTransactions from './ImportTransactions';
import ConfirmDialog from '../UI/ConfirmDialog';

const today = new Date().toISOString().split('T')[0];
const defaultFrom = `${new Date().getFullYear()}-01-01`;

const fmt = (n) =>
  `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Filter icon ───────────────────────────────────────────────────────────────
const FilterIcon = ({ active }) => (
  <svg
    className={`w-3 h-3 flex-shrink-0 ${active ? 'text-blue-500' : 'text-slate-400 group-hover:text-slate-500'}`}
    fill="currentColor" viewBox="0 0 20 20"
  >
    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.553.894l-4 2A1 1 0 016 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
  </svg>
);

// ── Filterable column header ──────────────────────────────────────────────────
const FilterTh = ({ label, active, onOpen, className = '', children }) => (
  <th
    className={`sticky top-0 z-10 px-4 py-3 text-left font-semibold cursor-pointer select-none group whitespace-nowrap
      ${active ? 'text-blue-600 bg-blue-50' : 'text-slate-600 bg-slate-50'} ${className}`}
    onClick={onOpen}
  >
    <div className="flex items-center gap-1">
      {children || label}
      <FilterIcon active={active} />
    </div>
  </th>
);

// ── Transactions ──────────────────────────────────────────────────────────────
const Transactions = () => {
  const orgId = useOrgId();
  const { accounts, funds } = useOrgData(orgId);
  const activeAccounts = accounts.filter(a => a.active !== false).map(a => a.name);
  const activeFunds    = funds.filter(f => f.active !== false).map(f => f.name);

  const [searchParams, setSearchParams] = useSearchParams();
  const payeeParam = searchParams.get('payee') || '';
  const fromParam  = searchParams.get('from') || '';
  const toParam    = searchParams.get('to') || '';

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]           = useState(true);
  // Arriving with a ?payee= link (e.g. from the Payees report) should show that
  // payee's transactions for whatever range was linked — falling back to all
  // time, not just the current year's default window, when no range is given.
  const [fromDate, setFromDate]         = useState(fromParam || (payeeParam ? '2000-01-01' : defaultFrom));
  const [toDate, setToDate]             = useState(toParam || today);
  const [filterFund, setFilterFund]     = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterUncleared, setFilterUncleared] = useState(false);
  const [filterPayee, setFilterPayee]   = useState(payeeParam);
  const [showForm, setShowForm]     = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editTx, setEditTx]       = useState(null);
  const [deleteTx, setDeleteTx]   = useState(null);

  // Popover state
  const [openFilter, setOpenFilter] = useState(null); // 'date' | 'account' | 'fund' | 'cleared'
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openPopover = useCallback((name, e) => {
    if (openFilter === name) { setOpenFilter(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setPopoverPos({ top: rect.bottom + 4, left: rect.left });
    setOpenFilter(name);
  }, [openFilter]);

  useEffect(() => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    const q = query(
      collection(db, 'orgs', orgId, 'transactions'),
      where('date', '>=', fromDate),
      where('date', '<=', toDate),
      orderBy('date', 'asc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('Transactions query error:', err.code, err.message);
      setLoading(false);
    });
    return unsub;
  }, [orgId, fromDate, toDate]);

  const filtered = transactions.filter(t =>
    (!filterFund      || t.classification === filterFund) &&
    (!filterAccount   || t.account === filterAccount) &&
    (!filterUncleared || !t.cleared) &&
    (!filterPayee     || t.payableTo === filterPayee)
  );

  const nonStarting   = filtered.filter(t => t.type !== 'Starting Balance');
  const totalBills    = nonStarting.reduce((s, t) => s + (t.expense || 0), 0);
  const totalReceipts = nonStarting.reduce((s, t) => s + (t.income  || 0), 0);

  const hasFilters = filterFund || filterAccount || filterUncleared || filterPayee;
  const clearPayeeFilter = () => {
    setFilterPayee('');
    if (searchParams.has('payee')) {
      const next = new URLSearchParams(searchParams);
      next.delete('payee');
      setSearchParams(next, { replace: true });
    }
  };
  const clearFilters = () => {
    setFilterFund(''); setFilterAccount(''); setFilterUncleared(false); clearPayeeFilter();
  };

  const toggleCleared = (tx) =>
    updateDoc(doc(db, 'orgs', orgId, 'transactions', tx.id), { cleared: !tx.cleared });

  const handleDelete = async () => {
    await logAudit(orgId, 'Transaction Deleted', {
      account: deleteTx.account, type: deleteTx.type, date: deleteTx.date,
      amount: deleteTx.expense || deleteTx.income,
    });
    await deleteDoc(doc(db, 'orgs', orgId, 'transactions', deleteTx.id));
    setDeleteTx(null);
  };

  const openAdd  = () => { setEditTx(null); setShowForm(true); };
  const openEdit = (tx) => { setEditTx(tx); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditTx(null); };

  // ── Popover content ─────────────────────────────────────────────────────────
  const renderPopover = () => {
    if (!openFilter) return null;

    let content;
    if (openFilter === 'date') {
      content = (
        <div className="space-y-3 w-56">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Date Range</div>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-slate-500 block mb-1">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">To</label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            {['This year', 'Last year', 'All'].map(label => (
              <button
                key={label}
                onClick={() => {
                  const y = new Date().getFullYear();
                  if (label === 'This year') { setFromDate(`${y}-01-01`); setToDate(`${y}-12-31`); }
                  if (label === 'Last year') { setFromDate(`${y-1}-01-01`); setToDate(`${y-1}-12-31`); }
                  if (label === 'All')       { setFromDate('2000-01-01'); setToDate(today); }
                }}
                className="flex-1 text-xs border border-gray-200 rounded-lg py-1 hover:bg-slate-50 text-slate-600"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );
    } else if (openFilter === 'account') {
      content = (
        <div className="w-48">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Account</div>
          <div className="space-y-0.5">
            {['', ...activeAccounts].map(a => (
              <button
                key={a || '__all__'}
                onClick={() => { setFilterAccount(a); setOpenFilter(null); }}
                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors
                  ${filterAccount === a ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-700'}`}
              >
                {a || 'All accounts'}
              </button>
            ))}
          </div>
        </div>
      );
    } else if (openFilter === 'fund') {
      content = (
        <div className="w-48">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Fund</div>
          <div className="space-y-0.5">
            {['', ...activeFunds].map(f => (
              <button
                key={f || '__all__'}
                onClick={() => { setFilterFund(f); setOpenFilter(null); }}
                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors
                  ${filterFund === f ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-700'}`}
              >
                {f || 'All funds'}
              </button>
            ))}
          </div>
        </div>
      );
    } else if (openFilter === 'cleared') {
      content = (
        <div className="w-44">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Cleared</div>
          <div className="space-y-0.5">
            {[
              { label: 'All transactions', value: false },
              { label: 'Uncleared only',  value: true  },
            ].map(opt => (
              <button
                key={opt.label}
                onClick={() => { setFilterUncleared(opt.value); setOpenFilter(null); }}
                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors
                  ${filterUncleared === opt.value ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-700'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div
        ref={popoverRef}
        style={{ position: 'fixed', top: popoverPos.top, left: popoverPos.left, zIndex: 1000 }}
        className="bg-white rounded-xl shadow-xl border border-gray-100 p-3"
      >
        {content}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 h-full flex flex-col">
      {/* Header — sticky so it (and Add Transaction) stay visible while scrolling the table */}
      <div className="sticky top-0 z-20 bg-gray-100 -mx-4 md:-mx-6 px-4 md:px-6 pt-4 md:pt-6 pb-3 mb-2 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-slate-800">Transactions</h2>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              >
                <span>Clear filters</span>
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowImport(true)}
              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Import
            </button>
            <button
              onClick={openAdd}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              + Add Transaction
            </button>
          </div>
        </div>
      </div>

      {/* Active filter pills */}
      {hasFilters && (
        <div className="flex gap-2 mb-3 flex-wrap">
          {filterAccount && (
            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full">
              {filterAccount}
              <button onClick={() => setFilterAccount('')} aria-label="Clear account filter" title="Clear account filter" className="hover:text-blue-900 ml-0.5">×</button>
            </span>
          )}
          {filterFund && (
            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full">
              {filterFund}
              <button onClick={() => setFilterFund('')} aria-label="Clear fund filter" title="Clear fund filter" className="hover:text-blue-900 ml-0.5">×</button>
            </span>
          )}
          {filterUncleared && (
            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full">
              Uncleared only
              <button onClick={() => setFilterUncleared(false)} aria-label="Clear uncleared filter" title="Clear uncleared filter" className="hover:text-blue-900 ml-0.5">×</button>
            </span>
          )}
          {filterPayee && (
            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2.5 py-1 rounded-full">
              Payee: {filterPayee}
              <button onClick={clearPayeeFilter} aria-label="Clear payee filter" title="Clear payee filter" className="hover:text-blue-900 ml-0.5">×</button>
            </span>
          )}
        </div>
      )}

      {/* Table — its own scroll region (not the page) so the sticky <thead> has a
          real scrolling ancestor. Nesting sticky under overflow-x-auto on the page
          scroller doesn't work: a non-visible-overflow ancestor that never itself
          scrolls "traps" position:sticky, so it silently never sticks. */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-gray-200">
                <FilterTh
                  active={fromDate !== defaultFrom || toDate !== today}
                  onOpen={e => openPopover('date', e)}
                >
                  <span>Date</span>
                </FilterTh>
                <FilterTh
                  active={!!filterAccount}
                  onOpen={e => openPopover('account', e)}
                  label="Account"
                />
                <FilterTh
                  active={!!filterFund}
                  onOpen={e => openPopover('fund', e)}
                  label="Category"
                />
                <th className="sticky top-0 z-10 px-4 py-3 text-right font-semibold text-slate-600 bg-slate-50">Amount</th>
                <th className="sticky top-0 z-10 px-4 py-3 text-left font-semibold text-slate-600 bg-slate-50">Description</th>
                <FilterTh
                  active={filterUncleared}
                  onOpen={e => openPopover('cleared', e)}
                  className="text-center"
                  label="Cleared"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">Loading…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                    No transactions found for this date range.
                  </td>
                </tr>
              ) : filtered.map((tx, i) => {
                const isSB   = tx.type === 'Starting Balance';
                const isBill = !isSB && tx.expense > 0;
                return (
                  <tr
                    key={tx.id}
                    onClick={() => openEdit(tx)}
                    className={`cursor-pointer hover:bg-blue-50/40 transition-colors ${i % 2 === 1 ? 'bg-slate-50/40' : ''}`}
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap font-mono text-sm text-slate-600">{tx.date}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">{tx.account}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-semibold flex-shrink-0 ${
                          isSB   ? 'bg-slate-200 text-slate-600' :
                          isBill ? 'bg-red-100 text-red-600' :
                                   'bg-green-100 text-green-700'
                        }`}>
                          {isSB ? 'SB' : isBill ? 'Out' : 'In'}
                        </span>
                        {tx.split ? (
                          <span className="text-sm text-slate-500 italic">Split ({tx.lines?.length || '?'} lines)</span>
                        ) : (
                          <span className="text-sm text-slate-700">{isSB ? tx.classification : tx.type}</span>
                        )}
                      </div>
                    </td>
                    <td className={`px-4 py-2.5 text-right text-sm font-medium font-mono whitespace-nowrap ${
                      isSB ? 'text-slate-500' : isBill ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {isSB ? fmt(tx.income) : isBill ? fmt(tx.expense) : fmt(tx.income)}
                    </td>
                    <td className="px-4 py-2.5 max-w-[240px]">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {tx.checkNum && (
                          <span className="text-sm font-mono text-slate-500 whitespace-nowrap">#{tx.checkNum}</span>
                        )}
                        {tx.payableTo && (
                          <span className="text-sm text-slate-600 truncate">{tx.payableTo}</span>
                        )}
                        {tx.donor && (
                          <span className="text-sm text-green-700 truncate">{tx.donor}</span>
                        )}
                      </div>
                      {tx.split ? (
                        <span className="text-sm text-slate-500">
                          {[...new Set(tx.lines?.map(l => l.type))].join(', ')}
                        </span>
                      ) : (
                        (!tx.payableTo || tx.description) && (
                          <span className="truncate block text-sm text-slate-500" title={tx.description}>{tx.description}</span>
                        )
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={!!tx.cleared}
                        onChange={() => toggleCleared(tx)}
                        className="w-4 h-4 cursor-pointer accent-black"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-slate-100 font-semibold border-t-2 border-slate-200">
                  <td colSpan={3} className="px-4 py-2.5 text-slate-500 text-sm">
                    {filtered.length} transactions
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs">
                    <span className="text-red-600">{fmt(totalBills)} out</span>
                    <span className="text-slate-300 mx-1">·</span>
                    <span className="text-green-600">{fmt(totalReceipts)} in</span>
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Column filter popovers */}
      {renderPopover()}

      {showForm && (
        <TransactionForm
          transaction={editTx}
          orgId={orgId}
          onClose={closeForm}
          onDelete={editTx ? () => { closeForm(); setDeleteTx(editTx); } : null}
        />
      )}

      {showImport && (
        <ImportTransactions orgId={orgId} onClose={() => setShowImport(false)} />
      )}

      {deleteTx && (
        <ConfirmDialog
          title="Delete transaction?"
          message={`${deleteTx.date} · ${deleteTx.account} · ${deleteTx.split ? `Split (${deleteTx.lines?.length} lines)` : deleteTx.type} · ${deleteTx.expense > 0 ? fmt(deleteTx.expense) : fmt(deleteTx.income)}`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTx(null)}
          confirmLabel="Delete"
          danger
        />
      )}
    </div>
  );
};

export default Transactions;
